import {
  Box,
  Divider,
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Icon,
  IconButton,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import { ReactNode, useContext, useEffect, useState } from 'react'
import { SafeArea } from 'capacitor-plugin-safe-area'
import { IconType } from 'react-icons'
import {
  TbAppWindow,
  TbAppWindowFilled,
  TbBasket,
  TbBasketFilled,
  TbCalendar,
  TbCalendarFilled,
  TbChartPie,
  TbChartPieFilled,
  TbCreditCard,
  TbCreditCardFilled,
  TbLogout2,
  TbMapPin,
  TbMapPinFilled,
  TbMenu2,
  TbPackage,
  TbSettings,
  TbUser,
  TbUserFilled,
} from 'react-icons/tb'
import { Link, useLocation } from 'react-router-dom'
import { AuthContext } from '../../App'
import useMobile from '../../hooks/useMobile'
import { scrollbarStyle } from '../../theme/scrollbarStyles'
import AdminForceUpdate from '../modals/AdminForceUpdate'
import { Events, EventsFilled, NotificationFilled, Notifications, TbPackageFilled } from '../icons/icons'

const NavButton = (props: { label: string; path: string; icon: IconType; filledIcon: IconType; onClick?: () => void }) => {
  const location = useLocation()
  const isActive =
    location.pathname === props.path ||
    location.pathname === `${props.path}/` ||
    (location.pathname.includes(props.path) && props.path !== '/admin')

  return (
    <Link to={props.path} onClick={props.onClick}>
      <Flex alignItems="center" gap="10px" mb="30px">
        <Icon w="24px" h="24px" as={isActive ? props.filledIcon : props.icon} />
        <Text color="#171C22" fontSize={isActive ? 'lg' : 'md'} lineHeight="20px" fontWeight={isActive ? '600' : '500'}>
          {props.label}
        </Text>
      </Flex>
    </Link>
  )
}

const FooterButton = ({
  label,
  icon,
  onClick,
  danger,
}: {
  label: string
  icon: IconType
  onClick: () => void
  danger?: boolean
}) => (
  <Flex
    as="button"
    w="100%"
    alignItems="center"
    gap="10px"
    px="10px"
    py="9px"
    borderRadius="9px"
    cursor="pointer"
    color={danger ? '#FF3B30' : '#3B4654'}
    transition="background 0.15s ease"
    _hover={{ bg: danger ? 'rgba(255,59,48,0.08)' : '#F5F7F9' }}
    onClick={onClick}
  >
    <Icon w="20px" h="20px" as={icon} />
    <Text fontSize="md" lineHeight="20px" fontWeight="500">
      {label}
    </Text>
  </Flex>
)

const NavItems = ({ onForceUpdateOpen, onNavigate }: { onForceUpdateOpen: () => void; onNavigate?: () => void }) => {
  const { logout } = useContext(AuthContext)

  return (
    <>
      <Box flexGrow={1} minH={0} overflowY="auto" overflowX="hidden" sx={scrollbarStyle}>
        <NavButton label="Analytics" path="/admin" icon={TbChartPie} filledIcon={TbChartPieFilled} onClick={onNavigate} />
        <NavButton
          label="Notifications"
          path="/admin/notifications"
          icon={Notifications}
          filledIcon={NotificationFilled}
          onClick={onNavigate}
        />
        <Divider mb="20px" />
        <NavButton label="Admins" path="/admin/admin" icon={TbUser} filledIcon={TbUserFilled} onClick={onNavigate} />
        <NavButton label="Hosts" path="/admin/hosts" icon={TbUser} filledIcon={TbUserFilled} onClick={onNavigate} />
        <NavButton label="Users" path="/admin/users" icon={TbUser} filledIcon={TbUserFilled} onClick={onNavigate} />
        <Divider mb="20px" />
        <NavButton label="Locations" path="/admin/venues" icon={TbMapPin} filledIcon={TbMapPinFilled} onClick={onNavigate} />
        <NavButton label="Calendar" path="/admin/calendar" icon={TbCalendar} filledIcon={TbCalendarFilled} onClick={onNavigate} />
        <NavButton label="Events" path="/admin/events" icon={Events} filledIcon={EventsFilled} onClick={onNavigate} />
        <NavButton label="Packages" path="/admin/packages" icon={TbPackage} filledIcon={TbPackageFilled} onClick={onNavigate} />
        <NavButton
          label="Memberships"
          path="/admin/memberships"
          icon={TbCreditCard}
          filledIcon={TbCreditCardFilled}
          onClick={onNavigate}
        />
        <Divider mb="20px" />
        <NavButton label="Carousel" path="/admin/promotions" icon={TbBasket} filledIcon={TbBasketFilled} onClick={onNavigate} />
        <NavButton label="Transactions" path="/admin/purchases" icon={TbAppWindow} filledIcon={TbAppWindowFilled} onClick={onNavigate} />
      </Box>
      <Box flexShrink={0} mt="12px" pt="12px" borderTop="1px solid #DCE2EA">
        <FooterButton label="Force Update" icon={TbSettings} onClick={onForceUpdateOpen} />
        <FooterButton label="Log out" icon={TbLogout2} onClick={() => logout()} danger />
      </Box>
    </>
  )
}

const AdminWrapper = ({ children }: { children: ReactNode }) => {
  const isMobile = useMobile(1000)
  const { isOpen: isForceUpdateOpen, onOpen: onForceUpdateOpen, onClose: onForceUpdateClose } = useDisclosure()
  const { isOpen: isDrawerOpen, onOpen: onDrawerOpen, onClose: onDrawerClose } = useDisclosure()

  // On notched / Dynamic Island iPhones the webview spans the full screen, so a
  // top bar anchored at y=0 sits *under* the status bar and its hamburger is not
  // tappable. Every other wrapper (User/Auth) offsets by the native status-bar
  // height; the admin bar was the one that didn't. Inset by that height only —
  // the admin bar stays compact, it just clears the island.
  const [statusBarHeight, setStatusBarHeight] = useState<number>(0)
  useEffect(() => {
    SafeArea.getStatusBarHeight().then(({ statusBarHeight }) => {
      setStatusBarHeight(statusBarHeight)
    })
  }, [])

  if (isMobile)
    return (
      <Flex h="100vh" w="100%" direction="column" overflow="hidden">
        <Flex
          minH={`${56 + statusBarHeight}px`}
          pt={`${statusBarHeight}px`}
          px="16px"
          alignItems="center"
          justifyContent="space-between"
          bg="white"
          borderBottom="1px solid #DCE2EA"
          position="relative"
          zIndex={1000}
        >
          <IconButton
            aria-label="Open menu"
            icon={<Icon w="24px" h="24px" as={TbMenu2} />}
            variant="ghost"
            onClick={onDrawerOpen}
          />
          <Box
            bgImage="/logo_black.svg"
            userSelect="none"
            w="70px"
            h="28px"
            bgPos="center"
            bgSize="contain"
            bgRepeat="no-repeat"
          />
          <Box w="40px" />
        </Flex>
        <Box flexGrow={1} overflowY="auto" overflowX="hidden" sx={scrollbarStyle}>
          {children}
        </Box>
        <Drawer isOpen={isDrawerOpen} placement="left" onClose={onDrawerClose}>
          <DrawerOverlay />
          <DrawerContent maxW="260px" p="24px" pt={`${30 + statusBarHeight}px`}>
            {/* Offset the close button (and content) below the status bar /
                Dynamic Island so it isn't rendered behind the island on iOS. */}
            <DrawerCloseButton top={`${8 + statusBarHeight}px`} />
            <Box
              bgImage="/logo_black.svg"
              userSelect="none"
              w="80px"
              h="33px"
              bgPos="center"
              bgSize="contain"
              bgRepeat="no-repeat"
              mb="30px"
            />
            <NavItems onForceUpdateOpen={onForceUpdateOpen} onNavigate={onDrawerClose} />
          </DrawerContent>
        </Drawer>
        <AdminForceUpdate isOpen={isForceUpdateOpen} onClose={onForceUpdateClose} />
      </Flex>
    )

  return (
    <Flex h="100vh" w="100%" overflow="hidden">
      <Flex h="100%" w="235px" bg="white" p="30px" zIndex={1000} direction="column">
        <Box
          bgImage="/logo_black.svg"
          userSelect="none"
          w="80px"
          h="33px"
          flexShrink={0}
          bgPos="center"
          bgSize="contain"
          bgRepeat="no-repeat"
          mb="40px"
        />
        <NavItems onForceUpdateOpen={onForceUpdateOpen} />
      </Flex>
      <Divider orientation="vertical" zIndex={1000} />
      <Box h="100%" w="calc(100% - 236px)" zIndex={1} overflowY="auto" overflowX="hidden" sx={scrollbarStyle}>
        {children}
      </Box>
      <AdminForceUpdate isOpen={isForceUpdateOpen} onClose={onForceUpdateClose} />
    </Flex>
  )
}

export default AdminWrapper
